import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import DoughnutChart from '../DoughnutChart';
import { formatDateYMD } from '../../utils/formatDate';
import { apiGet } from '../../services/_common';
import { IUser } from '../../atoms';
import IssueGuide from '../IssueGuide';
import SkeletonTable from '../SkeletonTable';
import { ERROR } from '../../_constants';

// 무사고 현황
export const AccidentInfo = ({ name, userInfo, refetchInterval }: { name: string; userInfo: IUser; refetchInterval?: number }) => {
  const isSiteDashboard = refetchInterval !== undefined; // 상황판 여부
  const { hCd, sCd } = userInfo;
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useQuery(['safeAccidentmStatusGet', userInfo], () => fetchData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(isSiteDashboard && { refetchInterval }), // 30분
  });

  const fetchData = async () => {
    try {
      const res = await apiGet({ path: '/safe/accidentm/status', req: { hCd, sCd } });
      return res.data.data;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const onClickRefresh = () => {
    refetch();
  };

  const loading = isLoading || !data;
  if (isError) return <IssueGuide isImg={false} text={ERROR} />;
  return (
    <div className='accidentInfo'>
      <div className='widget-header'>
        <div className='widget-title flex-between'>
          <span>{t(name)}</span>
          {!isSiteDashboard && (
            <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
              <span className='material-symbols-rounded'>refresh</span>
            </div>
          )}
        </div>
      </div>
      {loading ? (
        <SkeletonTable rows={1} />
      ) : Object.values(data).every((value) => value === null) ? (
        <IssueGuide isImg={false} />
      ) : (
        <div className='widget-body donut'>
          <div className='doughnutChartWrapper'>
            <DoughnutChart
              showLegend={false}
              percentage={[Number(data?.accidentAday) ?? 0, Number(data?.accidentRday) ?? 0]}
              label={[t('달성 일 수'), t('남은 일 수')]}
              centerText={false}
              colorSet={['#3E97FF', '#3e98ff30']}
              // colorSet={['#4f6be8', 'rgba(79, 107, 232, 0.1)']}
              borderStatus={false}
            />
          </div>
          <div className='content'>
            <dl className='flex-between'>
              <dd>
                {!data?.accidentSdate || !data?.accidentEdate ? (
                  <span className='badge'>{t('무사고 일자 설정 필요')}</span>
                ) : (
                  `${formatDateYMD(data?.accidentSdate)} ~ ${formatDateYMD(data?.accidentEdate)}`
                )}
              </dd>
              {!isSiteDashboard && (
                <dd className='flex-basic'>
                  <span>{data?.accidentTday ?? 0}</span>
                  {t('-일')}
                </dd>
              )}
            </dl>
            {isSiteDashboard && (
              <dl className='flex-between'>
                <dt className='flex-basic'>
                  <div className='dot'>
                    <div />
                  </div>
                  {t('목표 일 수')}
                </dt>
                <dd className='flex-basic'>
                  <span>{data?.accidentTday ?? 0}</span>
                  {t('일')}
                </dd>
              </dl>
            )}
            <dl className='flex-between'>
              <dt className='flex-basic'>
                <div className='fill' />
                {t('달성 일 수')}
              </dt>
              <dd className='flex-basic'>
                <span>{data?.accidentAday ?? 0}</span>
                {t('-일')}
              </dd>
            </dl>
            <dl className='flex-between'>
              <dt className='flex-basic'>
                <div className='empty' />
                {t('남은 일 수')}
              </dt>
              <dd className='flex-basic'>
                <span>{Number(data?.accidentRday) > 0 ? Number(data?.accidentRday) : 0}</span>
                {t('-일')}
              </dd>
            </dl>
          </div>
        </div>
      )}
    </div>
  );
};
